import { Component } from "react";
import { ColorResult, SketchPicker } from "react-color";
import { AnglePicker } from "react-linear-gradient-picker";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallMediumText from "../../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import OriginGrid from "../origin-grid";
import {
  DEFAULT_GRADIENT_POSITION,
  DEFAULT_RADIAL_SHAPE,
  DEFAULT_RADIAL_SIZE,
  GradientType,
  IGradientPosition,
  IGradientState,
  IGradientStop,
  RADIAL_SIZES,
  RadialShape,
  RadialSize
} from "./gradient-utils";

interface IGradientPickerProps {
  gradient: IGradientState;
  selectedStopIndex: number;
  isSketchPickerVisible: boolean;
  setIsSketchPickerVisible: (visible: boolean) => void;
  gradientInputValue: string;
  /** When false (mobile-native screen) only the linear editor is shown. */
  allowAdvancedGradient: boolean;
  onTypeChange: (type: GradientType) => void;
  onAngleChange: (angle: number) => void;
  onPositionChange: (position: IGradientPosition) => void;
  onShapeChange: (shape: RadialShape) => void;
  onSizeChange: (size: RadialSize) => void;
  onStopSelect: (index: number) => void;
  onStopColorChange: (index: number, color: ColorResult) => void;
  onSwapColors: () => void;
  onStopMouseDown: (event: React.MouseEvent, index: number) => void;
  onInputClick: () => void;
}

interface IGradientTypeOption {
  value: GradientType;
  label: string;
}

interface IRadialShapeOption {
  value: RadialShape;
  label: string;
}

const TYPE_OPTIONS: IGradientTypeOption[] = [
  { value: "linear", label: "Linear" },
  { value: "radial", label: "Radial" },
  { value: "conic", label: "Conic" }
];

const SHAPE_OPTIONS: IRadialShapeOption[] = [
  { value: "circle", label: "Circle" },
  { value: "ellipse", label: "Ellipse" }
];

// Human-readable labels for the CSS radial-size keywords.
const RADIAL_SIZE_LABELS: Record<RadialSize, string> = {
  "closest-side": "Closest side",
  "closest-corner": "Closest corner",
  "farthest-side": "Farthest side",
  "farthest-corner": "Farthest corner"
};

class GradientPicker extends Component<IGradientPickerProps> {
  renderGeometryControls(): React.ReactNode {
    const { gradient, allowAdvancedGradient, onAngleChange, onPositionChange, onShapeChange, onSizeChange } =
      this.props;
    const type: GradientType = gradient.type;
    const position: IGradientPosition = gradient.position ?? DEFAULT_GRADIENT_POSITION;

    return (
      <>
        {allowAdvancedGradient && (
          <div className="gradient-picker__type">
            <KuikaTabs
              size="sm"
              value={type}
              options={TYPE_OPTIONS}
              onChange={(value: string) => this.props.onTypeChange(value as GradientType)}
              dataTestId="gradient-type"
            />
          </div>
        )}

        {(type === "linear" || (type === "conic" && allowAdvancedGradient)) && (
          <div className="gradient-picker__angle">
            <div className="gradient-picker__angle-controls">
              <KuikaSmallMediumText>{type === "conic" ? "From" : "Angle"}</KuikaSmallMediumText>
              <AnglePicker
                angle={gradient.angle}
                onChange={onAngleChange}
                size={40}
                snap={5}
                setAngle={onAngleChange}
              />
              <KuikaInput
                type="number"
                value={gradient.angle}
                onChange={(e) => onAngleChange(Number(e.target.value))}
                size="sm"
                rightUnit="DEG"
              />
            </div>
          </div>
        )}

        {allowAdvancedGradient && (type === "radial" || type === "conic") && (
          <div className="gradient-picker__position">
            <KuikaSmallMediumText>Position</KuikaSmallMediumText>
            <OriginGrid value={position} onChange={onPositionChange} dataTestId="gradient-position" />
          </div>
        )}

        {allowAdvancedGradient && type === "radial" && (
          <>
            <div className="gradient-picker__shape">
              <KuikaSmallMediumText>Shape</KuikaSmallMediumText>
              <KuikaTabs
                size="sm"
                value={gradient.shape ?? DEFAULT_RADIAL_SHAPE}
                options={SHAPE_OPTIONS}
                onChange={(value: string) => onShapeChange(value as RadialShape)}
                dataTestId="gradient-shape"
              />
            </div>
            <div className="gradient-picker__size">
              <KuikaSmallMediumText>Size</KuikaSmallMediumText>
              <KuikaSelectbox
                size="sm"
                value={gradient.size ?? DEFAULT_RADIAL_SIZE}
                options={RADIAL_SIZES.map((size: RadialSize) => ({ value: size, label: RADIAL_SIZE_LABELS[size] }))}
                onChange={(next: string | number | (string | number)[] | null) => {
                  if (typeof next === "string") {
                    onSizeChange(next as RadialSize);
                  }
                }}
                dataTestId="gradient-size"
              />
            </div>
          </>
        )}
      </>
    );
  }

  render() {
    const {
      gradient,
      selectedStopIndex,
      isSketchPickerVisible,
      setIsSketchPickerVisible,
      gradientInputValue,
      onStopSelect,
      onStopColorChange,
      onSwapColors,
      onStopMouseDown,
      onInputClick
    } = this.props;

    // The preview bar is a horizontal STOP EDITOR strip: stops are dragged along
    // 0–100%, so a fixed 90deg linear render is the right visualisation for all
    // gradient types. The real radial/conic result renders live on the canvas.
    const stopStripBackground: string = `linear-gradient(90deg, ${gradient.stops
      .map((stop: IGradientStop) => `${stop.color} ${stop.position}%`)
      .join(", ")})`;

    return (
      <div className="gradient-picker">
        {this.renderGeometryControls()}
        <div className="gradient-picker__preview">
          <div style={{ position: "absolute", inset: 0, background: stopStripBackground }} />
          <div className="gradient-picker__stops">
            {gradient.stops.map((stop: IGradientStop, index: number) => (
              <div
                key={`${stop.position}-${stop.color}`}
                className={`gradient-picker__stop ${selectedStopIndex === index ? "selected" : ""}`}
                style={{
                  left: `${stop.position}%`,
                  backgroundColor: stop.color
                }}
                onClick={() => onStopSelect(index)}
                onMouseDown={(e) => onStopMouseDown(e, index)}
              />
            ))}
          </div>
        </div>
        <div className="gradient-picker__input">
          <KuikaSmallMediumText>Color</KuikaSmallMediumText>
          <KuikaInput
            type="text"
            value={gradientInputValue}
            size="sm"
            rightUnit="HEX"
            readOnly
            onClick={onInputClick}
            className="kuika-styling-panel__color-input"
            leftUnit={
              <div
                style={{
                  width: 24,
                  height: 24,
                  background: `#${gradientInputValue}`,
                  cursor: "pointer"
                }}
                onClick={() => setIsSketchPickerVisible(true)}
              ></div>
            }
          />

          <div>
            <KuikaButton type="tertiary" color="neutral" size="sm" isIcon onClick={onSwapColors}>
              <KuikaSvg name="repeat" />
            </KuikaButton>

            <div className="gradient-picker__controls">
              {selectedStopIndex !== -1 && (
                <div className="sketch-picker-wrapper" style={{ display: isSketchPickerVisible ? "block" : "none" }}>
                  <div className="kuika-flex-end">
                    <KuikaButton
                      type="tertiary"
                      color="neutral"
                      size="sm"
                      isIcon
                      onClick={() => setIsSketchPickerVisible(false)}
                    >
                      <KuikaSvg name="x" />
                    </KuikaButton>
                  </div>
                  <SketchPicker
                    color={gradient.stops[selectedStopIndex]?.color || "rgba(255, 255, 255, 1)"}
                    onChange={(color: ColorResult) => onStopColorChange(selectedStopIndex, color)}
                    styles={{
                      default: {
                        picker: {
                          width: "100%",
                          padding: "8px",
                          boxShadow: "none",
                          background: "var(--dark-1)",
                          top: 50
                        },
                        saturation: {
                          height: "100px",
                          borderRadius: "var(--radius-xs)",
                          padding: "0"
                        }
                      }
                    }}
                    presetColors={[]}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default GradientPicker;
